<!-- /src/views/Dashboard/transactions/Client.vue  -->
<template>
  <div class="container-nzidoo">

    <page-title title="sidebar.myTransactions" />
    <!-- ======= TABS ======= -->
    <div class="tabs">
      <button class="btn-tab" :class="{ active: activeTab === 'sales' }" @click="setActiveTab('sales')">
        {{ $t('ecommerce.sales') }}
      </button>
      <button class="btn-tab" :class="{ active: activeTab === 'purchases' }" @click="setActiveTab('purchases')">
        {{ $t('ecommerce.purchases') }}
      </button>
    </div>

    <!-- ======= FILTERS ======= -->
    <div class="filters scroll-horizontal" v-if="activeTab === 'sales'">
      <button v-for="f in salesFilters" :key="f.key" class="btn-tab" :class="{ active: salesFilterStatus === f.key }"
        @click="salesFilterStatus = f.key">
        {{ f.label }}
      </button>
    </div>

    <div class="filters scroll-horizontal" v-else>
      <button v-for="f in purchaseFilters" :key="f.key" class="btn-tab"
        :class="{ active: purchaseFilterStatus === f.key }" @click="purchaseFilterStatus = f.key">
        {{ f.label }}
      </button>
    </div>

    <!-- ======= TAB CONTENT ======= -->
    <div v-if="!loader" class="tab-content">
      <!-- SALES -->
      <div v-if="activeTab === 'sales'" class="tab-panel">
        <div v-if="filteredSalesProducts.length === 0" class="empty">
          {{ $t('sentence.noProductInThisSection') }}
        </div>
        <div v-else class="row px-2 px-md-3" ref="salesProductList">
          <div v-for="(item, index) in filteredSalesProducts" :key="item?.id || `sale-${index}`" class="col-md-4 p-1 px-0 px-md-1">
            <OrderCard
              v-if="item && item.id"
              :order="item"
              :showStats="false"
              @show="viewSaleDetails(item)"
              @rate="openSaleRatingModal(item)"
            />
          </div>
        </div>
      </div>

      <!-- PURCHASES -->
      <div v-else class="tab-panel">
        <div v-if="filteredPurchaseItems.length === 0" class="empty">
          {{ $t('sentence.noProductInThisSection') }}
        </div>
        <div v-else class="row px-2 px-md-3" ref="purchasesProductList">
          <div v-for="(order, index) in filteredPurchaseItems" :key="order?.id || `order-${index}`" class="col-md-4 p-1 px-0 px-md-1">
            <OrderCard
              v-if="order && order.id"
              :order="order"
              :showStats="false"
              @show="viewOrderDetails(order)"
              @rate="openOrderRatingModal(order)"
            />
          </div>
        </div>
      </div>
    </div>
    <LoadingSpinner v-else />
    <!-- ======= MODALS ======= -->
    <ProductDetailsModal :inSaleProduct="selectedProduct" />
    <RatingModal :ratingModel="ratingModal" :ratingModelId="user.id" :modelToRates="modalToRate"
      :modelToRatesId="modalToRateId" @rating-submitted="handleRatingSubmitted" />
    <v-dialog />
  </div>
</template>

<script>
import ProductDetailsModal from "@/components/modals/ProductDetailsModal";
import RatingModal from "@/components/modals/RatingModal";
import OrderCard from "@/components/templates/OrderCard.vue";
import gsapAnimations from "@/services/gsapAnimations";
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import PageTitle from '@/components/templates/PageTitle.vue';


export default {
  components: { 
    ProductDetailsModal, 
    RatingModal, 
    OrderCard,
    LoadingSpinner, 
    PageTitle 
  },

  data() {
    return {
      salesList: [],
      purchases: [],
      selectedProduct: null,
      selectedPurchase: null,
      salesFilterStatus: "pending",
      purchaseFilterStatus: "pending",
      ratingModal: "",
      modalToRate: "",
      ratingModalId: -1,
      modalToRateId: -1,
      loader: false,
    };
  },

  async mounted() {
    this.loader = true;
    
    // Set active tab from URL param, default to 'sales'
    const tabFromUrl = this.$route.params.activeTab;
    if (tabFromUrl && ['sales', 'purchases'].includes(tabFromUrl)) {
      // Tab is already set from URL, no need to navigate
    } else {
      // Navigate to default tab if not present or invalid
      this.$router.replace({ 
        name: this.$route.name, 
        params: { ...this.$route.params, activeTab: 'sales' } 
      });
    }
    
    this.$nextTick(() => {
      this.initAnimations();
    });
    await this.getAllUserData();
    this.loader = false;
  },

  computed: {
    activeTab() {
      // Read activeTab from URL params
      const tab = this.$route.params.activeTab;
      return ['sales', 'purchases'].includes(tab) ? tab : 'sales';
    },
    
    user: {
      get() {
        return this.$store.state.user;
      },
      set(user) {
        this.$store.commit("setUser", { user: user });
      },
    },
    salesFilters() {
      return [
        { key: "all", label: this.$t("filter.all") || "Toutes" },
        { key: "pending", label: this.$t("filter.pending") || "En attente" },
        { key: "completed", label: this.$t("filter.completed") || "Terminé" },
        { key: "cancelled", label: this.$t("filter.cancelled") || "Annulées" },
      ];
    },
    purchaseFilters() {
      return this.salesFilters;
    },

    /* ----- Sales Filter Logic ----- */
    filteredSalesProducts() {
      const list = Array.isArray(this.salesList)
        ? this.salesList.slice()
        : [];

      // Filter out any null/undefined items and ensure order_products is an array
      const validList = list
        .filter((order) => order && typeof order === 'object' && order.id !== undefined)
        .map(order => ({
          ...order,
          order_products: Array.isArray(order.order_products) ? order.order_products : []
        }));

      switch (this.salesFilterStatus) {
        case "pending":
          return validList.filter(
            (order) =>
              order &&
              order.buyer_confirmation === null &&
              (order.status === "inProgress" || order.status === "onHold" || order.status === "in_progress")
          );

        case "completed":
          return validList.filter(
            (order) => order && (order.status === "received" || order.status === "sold")
          );

        case "cancelled":
          return validList.filter((order) => {
            if (!order) return false;
            return ["cancelled", "refused"].includes(order.status);
          });

        default:
          return validList;
      }
    },

    /* ----- Purchases Filter Logic ----- */
    filteredPurchaseItems() {
      const list = Array.isArray(this.purchases)
        ? [...this.purchases]
        : [];

      // Filter out any null/undefined items and ensure order_products is an array
      const validList = list
        .filter((order) => order && typeof order === 'object' && order.id !== undefined)
        .map(order => ({
          ...order,
          order_products: Array.isArray(order.order_products) ? order.order_products : []
        }));

      switch (this.purchaseFilterStatus) {
        case "pending":
          return validList.filter(
            (order) => order && order.status === "in_progress"
          );

        case "completed":
          return validList.filter(
            (order) =>
              order &&
              order.status === "sold" &&
              order.buyer_confirmation === true
          );

        case "cancelled":
          return validList.filter(
            (order) =>
              order &&
              ["cancelled", "refused"].includes(order.status)
          );

        default:
          return validList;
      }
    }

  },

  watch: {
    filteredSalesProducts(newVal) {
      if (newVal.length > 0 && this.activeTab === 'sales') {
        this.$nextTick(() => {
          const container = this.$refs.salesProductList;
          if (container) {
            gsapAnimations.animateNewItems(container, ".order-card");
          }
        });
      }
    },
    filteredPurchaseItems(newVal) {
      if (newVal.length > 0 && this.activeTab === 'purchases') {
        this.$nextTick(() => {
          const container = this.$refs.purchasesProductList;
          if (container) {
            gsapAnimations.animateNewItems(container, ".order-card");
          }
        });
      }
    },
    activeTab() {
      this.$nextTick(() => {
        this.initAnimations();
      });
    },
  },

  methods: {
    setActiveTab(tab) {
      // Update URL with new tab
      this.$router.push({ 
        name: this.$route.name, 
        params: { ...this.$route.params, activeTab: tab } 
      });
    },
    
    initAnimations() {
      // Kill any existing scroll triggers to prevent duplicates
      gsapAnimations.killAllScrollTriggers();

      // Animate current tab's products
      this.$nextTick(() => {
        if (this.activeTab === 'sales' && this.filteredSalesProducts.length > 0) {
          const container = this.$refs.salesProductList;
          if (container) {
            gsapAnimations.setupScrollAnimation(container, ".order-card");
          }
        } else if (this.activeTab === 'purchases' && this.filteredPurchaseItems.length > 0) {
          const container = this.$refs.purchasesProductList;
          if (container) {
            gsapAnimations.setupScrollAnimation(container, ".order-card");
          }
        }
      });
    },

    async getAllUserData() {
      try {
        const { data } = await this.$axios.get("order/data"); 
        // Filter out any null/undefined items
        this.salesList = (data.orders || []).filter(
          (order) => order && typeof order === 'object' && order.id !== undefined
        );
        this.purchases = (data.purchases || []).filter(
          (order) => order && typeof order === 'object' && order.id !== undefined
        );
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    },

    handleRatingSubmitted() {
      // Refresh the data after rating is submitted
      this.getAllUserData();
    },

    openSaleRatingModal(order) {
      // For sales, you might want to rate the buyer
      // Adjust this logic based on your business requirements
      if (order.order_products && order.order_products.length > 0) {
        const firstProduct = order.order_products[0];
        
        this.ratingModal = "User";
        this.ratingModalId = this.user.id;
        this.modalToRate = "Product";
        this.modalToRateId = firstProduct.product.id;

        this.$modal.show("rating-modal");
      }
    },

    openOrderRatingModal(order) {
      // For purchases, rate the product/seller
      if (order.order_products && order.order_products.length > 0) {
        const firstProduct = order.order_products[0];
        
        this.ratingModal = "User";
        this.ratingModalId = this.user.id;
        this.modalToRate = "Product";
        this.modalToRateId = firstProduct.product.id;

        this.$modal.show("rating-modal");
      }
    },

    viewSaleDetails(order) {
      // View details of a sale order
      this.selectedPurchase = order;
      // You can navigate to order details or show a modal
      // this.$router.push({ name: "OrderDetails", params: { id: order.id } });
    },

    viewOrderDetails(order) {
      // View details of a purchase order
      this.selectedPurchase = order;
      // You can navigate to order details or show a modal
      // this.$router.push({ name: "OrderDetails", params: { id: order.id } });
    },
  },

  beforeDestroy() {
    // Cleanup: kill all scroll triggers when component is destroyed
    gsapAnimations.killAllScrollTriggers();
  }
};
</script>

<style scoped>
/* ======= PAGE CONTAINER ======= */
.content {
  max-width: 1500px;
  margin: 40px auto;
  padding: 20px;
  font-family: "Inter", system-ui, sans-serif;
  color: var(--color-2);
  background-color: var(--gray-100);
}

.scroll-horizontal {
  overflow-x: auto;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

/* ======= EMPTY STATE ======= */
.empty {
  text-align: center;
  padding: 40px 0;
  color: #7a88a6;
  font-size: 0.95rem;
}

/* ======= RESPONSIVE ======= */
@media (max-width: 640px) {
  .content {
    margin: 10px auto;
    padding: 14px;
  }
}
</style>